import { pinnedContracts } from '@hoodmorning/config';
import { SELECTED_ACCOUNT_CHAIN_ID, bytes32Schema, type Hex } from '@hoodmorning/contracts';
import { decodeEventLog, isAddressEqual, parseAbi, toEventSelector, zeroAddress, type PublicClient, type TransactionReceipt } from 'viem';
import { formatUserOperationReceipt, getUserOperationHash, type BundlerClient, type UserOperation, type UserOperationReceipt } from 'viem/account-abstraction';
import { getChainId } from 'viem/actions';
import { verifyContractCode } from './codeVerification.js';
import { validateUserOperationBudget, type UserOperationBudget } from './userOperation.js';

/** A single read of an untrusted bundler report, without an account or signer.
 * This normalizes RPC quantities only. Callers must independently reconcile the
 * report against the original commitment and canonical on-chain execution before
 * presenting a fill. Absence and transport errors never authorize a resend.
 */
export async function readReportedOwnerOperationReceipt(input: {
  bundler: BundlerClient; expectedHash: Hex;
}): Promise<UserOperationReceipt<'0.7'> | null> {
  const bundler = input.bundler;
  const expectedHash = input.expectedHash;
  bytes32Schema.parse(expectedHash);
  const request = bundler.request.bind(bundler);
  if (await getChainId(bundler) !== SELECTED_ACCOUNT_CHAIN_ID) throw new Error('Receipt observation chain mismatch');
  const raw = await request({ method: 'eth_getUserOperationReceipt', params: [expectedHash] }, { retryCount: 0 });
  if (await getChainId(bundler) !== SELECTED_ACCOUNT_CHAIN_ID) throw new Error('Receipt observation chain changed');
  if (raw === null) return null;
  // The installed viem formatter converts gas fields but leaves the RPC nonce
  // untouched. Validate and normalize that quantity explicitly before matching.
  if (!raw || typeof raw.success !== 'boolean' || typeof raw.nonce !== 'string'
    || !/^0x(?:0|[1-9a-f][0-9a-f]{0,63})$/i.test(raw.nonce)) throw new Error('Malformed bundler receipt');
  return { ...formatUserOperationReceipt(raw), nonce: BigInt(raw.nonce) };
}

/** One read-only observation, suitable for explicit polling or reload recovery.
 * Only a literal null response means pending. Transport/format errors propagate;
 * they never become transaction failure or permission to resubmit.
 */
export async function observeOwnerOperationReceipt(input: {
  bundler: BundlerClient;
  client: Parameters<typeof reconcileOwnerOperationReceipt>[0]['client'];
  operation: UserOperation<'0.7'>; expectedHash: Hex; budget: UserOperationBudget;
  requiredConfirmations?: bigint;
}) {
  const operation = structuredClone(input.operation);
  const budget = { ...input.budget };
  const expectedHash = input.expectedHash;
  const requiredConfirmations = input.requiredConfirmations;
  const client = input.client, bundler = input.bundler;
  validateUserOperationBudget(operation, budget);
  const computed = getUserOperationHash({ userOperation: operation, chainId: SELECTED_ACCOUNT_CHAIN_ID,
    entryPointAddress: pinnedContracts.entryPointV07.address, entryPointVersion: '0.7' });
  if (computed.toLowerCase() !== expectedHash.toLowerCase()) throw new Error('Receipt observation commitment mismatch');
  if (await client.getChainId() !== SELECTED_ACCOUNT_CHAIN_ID) throw new Error('Receipt observation chain mismatch');
  const reported = await readReportedOwnerOperationReceipt({ bundler, expectedHash });
  return reconcileOwnerOperationReceipt({ client, operation, expectedHash, budget, reported,
    requiredConfirmations });
}

export const ownerUserOperationEventAbi = parseAbi([
  'event UserOperationEvent(bytes32 indexed userOpHash,address indexed sender,address indexed paymaster,uint256 nonce,bool success,uint256 actualGasCost,uint256 actualGasUsed)',
]);

/** Compare the bundler claim to the actual transaction receipt, not merely its
 * success flag. This proves operation inclusion; it does not derive token cashflows
 * from other operations' logs in the same bundle or prove counterfactual ownership.
 */
export function matchOwnerOperationReceipt(input: {
  operation: UserOperation<'0.7'>; expectedHash: Hex; budget: UserOperationBudget;
  reported: UserOperationReceipt<'0.7'>; receipt: TransactionReceipt;
}) {
  const { operation, reported, receipt } = input;
  const gasBounds = validateUserOperationBudget(operation, input.budget);
  const expected = getUserOperationHash({ userOperation: operation, chainId: SELECTED_ACCOUNT_CHAIN_ID,
    entryPointAddress: pinnedContracts.entryPointV07.address, entryPointVersion: '0.7' });
  if (expected.toLowerCase() !== input.expectedHash.toLowerCase() || expected.toLowerCase() !== reported.userOpHash.toLowerCase()) throw new Error('Receipt user-operation hash mismatch');
  if (!isAddressEqual(reported.entryPoint, pinnedContracts.entryPointV07.address) || !isAddressEqual(reported.sender, operation.sender)
    || reported.nonce !== operation.nonce || (reported.paymaster !== undefined && !isAddressEqual(reported.paymaster, zeroAddress))) throw new Error('Receipt execution identity mismatch');
  if (receipt.status !== 'success') throw new Error('Bundle transaction reverted; operation outcome not proven');
  if (receipt.transactionHash.toLowerCase() !== reported.receipt.transactionHash.toLowerCase()
    || receipt.blockHash.toLowerCase() !== reported.receipt.blockHash.toLowerCase()
    || receipt.blockNumber !== reported.receipt.blockNumber || reported.receipt.status !== receipt.status) throw new Error('Bundler transaction receipt mismatch');
  const matches = receipt.logs.filter(log => isAddressEqual(log.address, pinnedContracts.entryPointV07.address)
    && log.topics[0]?.toLowerCase() === toEventSelector(ownerUserOperationEventAbi[0]).toLowerCase()
    && log.topics[1]?.toLowerCase() === expected.toLowerCase());
  if (matches.length !== 1) throw new Error('Exactly one matching EntryPoint event required');
  const log = matches[0]!;
  if (log.removed || log.blockHash !== receipt.blockHash || log.blockNumber !== receipt.blockNumber || log.transactionHash !== receipt.transactionHash || log.logIndex === null) throw new Error('EntryPoint log receipt identity mismatch');
  const event = decodeEventLog({ abi: ownerUserOperationEventAbi, topics: log.topics, data: log.data, strict: true });
  const args = event.args;
  if (!isAddressEqual(args.sender, operation.sender) || !isAddressEqual(args.paymaster, zeroAddress) || args.nonce !== operation.nonce
    || args.success !== reported.success || args.actualGasCost !== reported.actualGasCost || args.actualGasUsed !== reported.actualGasUsed) throw new Error('Bundler claim does not match EntryPoint event');
  if (args.actualGasUsed === 0n || args.actualGasCost > gasBounds.maximumGasCost || args.actualGasUsed > gasBounds.maximumGas
    || args.actualGasCost > args.actualGasUsed * operation.maxFeePerGas) throw new Error('Observed operation gas exceeds accepted budget');
  return { userOperationHash: expected, sender: args.sender, nonce: args.nonce, success: args.success,
    actualGasCost: args.actualGasCost, actualGasUsed: args.actualGasUsed,
    transactionHash: receipt.transactionHash, blockNumber: receipt.blockNumber, blockHash: receipt.blockHash, logIndex: log.logIndex };
}

/** Read-only reconciliation. Missing receipt is pending, not failure or permission
 * to resend. Confirmation is an observation at this head, not irreversible finality.
 */
export async function reconcileOwnerOperationReceipt(input: {
  client: Pick<PublicClient, 'getChainId' | 'getBlock' | 'getBytecode' | 'getTransactionReceipt'>;
  operation: UserOperation<'0.7'>; expectedHash: Hex; budget: UserOperationBudget;
  reported: UserOperationReceipt<'0.7'> | null;
  requiredConfirmations?: bigint;
}) {
  const operation = structuredClone(input.operation);
  const reported = input.reported === null ? null : structuredClone(input.reported);
  const budget = { ...input.budget };
  const expectedHash = input.expectedHash;
  const required = input.requiredConfirmations ?? 2n;
  if (required < 2n || required > 256n) throw new Error('Confirmation requirement outside supported bounds');
  if (await input.client.getChainId() !== SELECTED_ACCOUNT_CHAIN_ID) throw new Error('Receipt RPC chain mismatch');
  if (reported === null) return { status: 'pending' as const, resubmissionAuthorized: false as const };
  const receipt = await input.client.getTransactionReceipt({ hash: reported.receipt.transactionHash });
  const matched = matchOwnerOperationReceipt({ operation, reported, receipt, budget, expectedHash });
  const block = await input.client.getBlock({ blockNumber: receipt.blockNumber });
  if (block.hash !== receipt.blockHash) return { status: 'reorg-uncertain' as const, resubmissionAuthorized: false as const };
  const code = await verifyContractCode({ getBytecode: args => input.client.getBytecode({ address: args.address, blockNumber: receipt.blockNumber }) }, [
    { name: 'entryPointV07', address: pinnedContracts.entryPointV07.address, expectedCodeHash: pinnedContracts.entryPointV07.codeHash },
  ]);
  if (!code.valid) throw new Error('Receipt EntryPoint code mismatch');
  const head = await input.client.getBlock();
  const recheck = await input.client.getBlock({ blockNumber: receipt.blockNumber });
  if (recheck.hash !== receipt.blockHash || await input.client.getChainId() !== SELECTED_ACCOUNT_CHAIN_ID) return { status: 'reorg-uncertain' as const, resubmissionAuthorized: false as const };
  if (head.number === null || head.number < receipt.blockNumber) throw new Error('Receipt head is inconsistent');
  const confirmations = head.number - receipt.blockNumber + 1n;
  return { ...matched, status: confirmations < required ? 'confirming' as const : matched.success ? 'confirmed-success' as const : 'confirmed-reverted' as const,
    confirmations, observedHeadNumber: head.number, observedHeadHash: head.hash,
    finality: 'confirmation-observed-not-irreversible' as const, resubmissionAuthorized: false as const };
}
